import React, { useRef, useState } from "react";
import { AutocompleteMenu } from "@puzzle/ui";
import { Button } from "@puzzle/ve";
import { Upload } from "@puzzle/icons";
import { vars } from "@puzzle/theme";
import {
  StatementFragment,
  useGetStatementsQuery,
} from "../LedgerReconciliation/graphql.generated";

type StatementsDropdownProps = {
  companyId: string;
  accountId?: string;
  handleUploadedFile: (files: FileList | File[]) => void;
  onSelectedStatement: (statementFile: StatementFragment["file"]) => void;
  isUploading: boolean;
};

export const SELECT_FILE_TEXT = "Select statement";
const selectFileButtonCss = {
  pointerEvents: "all" as const,
  marginTop: `-${vars.space["1"]}`,
};

export const StatementsDropdownButton = ({
  accountId = "",
  companyId,
  handleUploadedFile,
  onSelectedStatement,
  isUploading,
}: StatementsDropdownProps) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const openFilePicker = () => hiddenFileInput.current?.click();

  const [selectedStatement, setSelectedStatement] = useState<StatementFragment | null>(null);
  const { data: statementsData, loading: statementsLoading } = useGetStatementsQuery({
    variables: {
      input: {
        companyId,
        accountId,
      },
    },
    skip: !accountId,
  });
  const filteredStatements =
    statementsData?.statements.filter((statement) => statement.file?.downloadInfo?.signedUrl) || [];

  const hasStatements = !statementsLoading && filteredStatements.length > 0;

  return (
    <>
      {hasStatements && (
        <AutocompleteMenu<StatementFragment, false, false, false>
          value={selectedStatement}
          options={filteredStatements}
          getOptionLabel={(option) => {
            return `${option.endDate} Statement`;
          }}
          getOptionKey={(o) => o.id}
          onChange={(e, newValue) => {
            setSelectedStatement(newValue);
            if (newValue?.file) {
              onSelectedStatement(newValue.file);
            }
          }}
          trigger={
            <Button
              variant="secondary"
              size="compact"
              css={selectFileButtonCss}
              loading={statementsLoading || isUploading}
            >
              {SELECT_FILE_TEXT}
            </Button>
          }
          footer={
            <Button
              variant="minimal"
              size="mini"
              onClick={openFilePicker}
              prefixElement={<Upload />}
              css={{
                pointerEvents: "all",
                marginTop: `-${vars.space["1"]}`,
                marginLeft: "auto",
              }}
            >
              Upload from computer
            </Button>
          }
        />
      )}
      {!hasStatements && (
        <Button
          variant="secondary"
          size="compact"
          loading={statementsLoading || isUploading}
          onClick={openFilePicker}
          css={selectFileButtonCss}
        >
          {SELECT_FILE_TEXT}
        </Button>
      )}

      <input
        type="file"
        ref={hiddenFileInput}
        accept=".pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          if (!e.target.files) return;
          handleUploadedFile(e.target.files);
        }}
      />
    </>
  );
};
